import { SyntheticEvent, useMemo } from 'react';
import { SwipeableHandlers } from 'react-swipeable';

import buildOverflowFigure from '@r1-frontend/ui-react/components/MovingBar/helpers/buildOverflowFigure';
import getAreasOfDots from '@r1-frontend/ui-react/components/MovingBar/helpers/getAreasOfDots';
import { TPolygonDotsShapes } from '@r1-frontend/ui-react/components/MovingBar/types';
import { COLORS } from '@r1-frontend/ui-react/tokens/colors';
import { hex2rgb } from '@r1-frontend/shared/helpers/hex2rgb';

import buildBaseFigure from './helpers/buildBaseFigure';
import getDotProps from './helpers/getDotProps';
import {
    controlElementInnerRadius,
    controlElementOuterRadius,
    curves,
    firstDotIndent, halfViewBoxHeight,
    indentFromFirstDotForLeftPointX, indentFromMiddleForLeftPointY,
    indentFromTopForRightPointY, viewBoxHeight,
} from './constants';

type TMovingBarControlElement = {
    useControlElement?: never,
    distance?: never,
} | {
    useControlElement: boolean,
    distance: number,
}

export type TMovingBarProps = {
    onDotClick?: (dotIdx: number) => (e: SyntheticEvent) => void,
    barMainBg?: COLORS,
    barOverflowBg?: COLORS,
    controlElInnerBg?: COLORS,
    controlElOuterBg?: COLORS,
    viewBoxWidth?: number,
    dotsCount?: number,
    dotBg?: COLORS,

    onSwipeHandlers?: SwipeableHandlers,

    debugMode?: boolean,
}

const MovingBar = (
    {
        onDotClick = () => () => true,
        barMainBg = COLORS.Outline_Light,
        barOverflowBg = COLORS.Locked,

        dotsCount = 0,
        dotBg = COLORS.Disabled,

        controlElInnerBg = COLORS.BgMain,
        controlElOuterBg = COLORS.Logo,
        viewBoxWidth = 250,
        distance = 0,

        onSwipeHandlers = {} as SwipeableHandlers,

        useControlElement = false,
        debugMode = false,
    }: TMovingBarProps & TMovingBarControlElement,
): JSX.Element => {
    if (distance > 1 || distance < 0) {
        throw new Error(`@r1-frontend/ui-react. The MovingBar component Error. The "distance" argument must be in float range from 0 to 1. Incoming was ${distance}`);
    }

    if (dotsCount && dotsCount !== 0 && dotsCount < 2) {
        throw new Error(`@r1-frontend/ui-react. The MovingBar component Error. The "dotsCount" argument must be greater than 1 or be 0. Incoming was ${dotsCount}`);
    }

    /** polygonDots **/
    const pd: TPolygonDotsShapes = {
        left: {
            x: controlElementOuterRadius - firstDotIndent + indentFromFirstDotForLeftPointX, // отступ от центра первой точки до края начала скругления
            top: halfViewBoxHeight - indentFromMiddleForLeftPointY, // подобрано опытным путем
            bottom: halfViewBoxHeight + indentFromMiddleForLeftPointY, // подобрано опытным путем
        },
        right: {
            x: viewBoxWidth - curves.back / 1.2, // отступ от правой границы
            top: indentFromTopForRightPointY,
            bottom: viewBoxHeight - indentFromTopForRightPointY,
        },
    };

    const getDotPropsFn = useMemo(
        () => getDotProps(controlElementOuterRadius, pd.right.x, dotsCount, controlElementInnerRadius),
        [controlElementInnerRadius, controlElementOuterRadius, pd.right.x, dotsCount],
    );

    const areasOfDots = useMemo(
        () => getAreasOfDots(pd.left.x, pd.right.x, viewBoxHeight, dotsCount),
        [pd.left.x, pd.right.x, viewBoxHeight, dotsCount],
    );

    return (
        <svg width={viewBoxWidth} height={viewBoxHeight} viewBox={`0 0 ${viewBoxWidth} ${viewBoxHeight}`} style={{ cursor: 'pointer' }}>
            <path d={buildBaseFigure(pd).join('')} stroke={barMainBg} fill={barMainBg}/>

            {dotsCount && (
                <g id={'MovingBar_dots'}>
                    {[...Array(dotsCount)].map((_, idx) => {
                        const { position, radius } = getDotPropsFn(idx);

                        return <g key={`MovingBar_dotGroup_${idx}`}>
                            <circle
                                data-test={'MovingBar_dot'}
                                key={`MovingBar-dot-${idx}`}
                                cx={position}
                                cy={halfViewBoxHeight}
                                r={radius}
                                stroke='none'
                                fill={dotBg}
                            />
                            <circle
                                data-test={'MovingBar_dot_shadow'}
                                key={`MovingBar-dot-${idx}-shadow`}
                                cx={position}
                                cy={halfViewBoxHeight}
                                r={controlElementOuterRadius}
                                stroke='none'
                                fill={'transparent'}
                                style={{ cursor: 'pointer' }}
                            />
                        </g>;
                    })}
                </g>
            )}

            {useControlElement && (
                <g id={'MovingBar_slider'}>
                    <path
                        d={buildOverflowFigure(pd, distance).join('')}
                        stroke={barOverflowBg}
                        fill={`rgba(${hex2rgb(barOverflowBg)},${debugMode ? '.2' : '1'})`}
                    />

                    {dotsCount && (
                        <g id={'MovingBar_passedDots'}>
                            {[...Array(dotsCount)].map((_, idx) => {
                                const { position, radius } = getDotPropsFn(idx);

                                return position <= viewBoxWidth * distance && (
                                    <circle
                                        data-test={'MovingBar_passedDot'}
                                        key={`MovingBar_passedDot_${idx}`}
                                        cx={position}
                                        cy={halfViewBoxHeight}
                                        r={radius}
                                        stroke='none'
                                        fill={'#DF8282'}
                                    />
                                );
                            })}
                        </g>
                    )}
                </g>
            )}

            {dotsCount && (
                <g id={'MovingBar_areaOfDots'}>
                    {areasOfDots.map((area, idx) => {
                        return <rect
                            key={`MovingBar_areaOfDot_${idx}`}
                            stroke={debugMode ? COLORS.Error : 'transparent'}
                            fill={'transparent'}
                            onClick={onDotClick(idx)}
                            {...area}
                        />;
                    })}
                </g>
            )}

            {useControlElement && (
                <>
                    {/* @ts-ignore */}
                    <g id={'MovingBar_controlElement'} data-test={'MovingBar_controlGroup'} {...onSwipeHandlers} style={{ cursor: 'pointer' }}>
                        <circle
                            data-test={'MovingBar_outerControl'}
                            cx={controlElementOuterRadius + (pd.right.x - controlElementOuterRadius) * distance}
                            cy={halfViewBoxHeight}
                            r={controlElementOuterRadius}
                            fill={`rgba(${hex2rgb(controlElOuterBg)},${debugMode ? '.2' : '1'})`}
                        />
                        <circle
                            data-test={'MovingBar_innerControl'}
                            cx={controlElementOuterRadius + (pd.right.x - controlElementOuterRadius) * distance}
                            cy={halfViewBoxHeight}
                            r={controlElementInnerRadius}
                            fill={`rgba(${hex2rgb(controlElInnerBg)},${debugMode ? '.2' : '1'}`}
                        />
                    </g>
                </>
            )}
        </svg>
    );
};
export default MovingBar;
